import React, { useState } from 'react';

import { Input, Row, Col, Badge } from 'antd';
import Axios from 'axios';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { getUrlDocument, getFileTypeIcons } from '@libs/NwUtils';

import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import CustomSwitch from '@components/Gui/CustomSwitch';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import NwIcon from '@components/Gui/NwIcon';
import WithCharCounter from '@components/Gui/WithCharCounter';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { TextArea } = Input;
const TextAreaWithCharCounter = WithCharCounter(TextArea);

const DocumentCard = styled.div`
    border: ${(props) => props.theme.DefaultBorder};
    padding: 16px;
    margin-bottom: 16px;
    border-radius: 8px;

    &.single-column {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;

        .document-card-body {
            flex: 1;
        }

        .document-card-footer {
            padding-top: 0;
        }
    }

    .document-card-body {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 1rem;

        .content {
            padding: 0;
        }

        .description {
            font-size: 0.85rem;
            opacity: 0.7;
        }

        .title {
            width: 100%;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        }

        .doc-hidden {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: ${(props) => props.theme.FontSizeSm};
            color: ${(props) => props.theme.DangerColor};
        }
    }
    .document-card-footer {
        padding-top: 1rem;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }
`;

const Wrapper = styled.div`
    width: 100%;
    .ant-badge-count {
        margin-left: 8px;
        background-color: ${(props) => props.theme.PrimaryColor};
    }

    .ant-divider {
        cursor: pointer;
    }
`;

const Container = styled(Row)`
    padding-top: 16px;
`;

const EventDocuments = ({ event, type, updateEvent, showDocumentsByDefault, singleColumn = false }) => {
    const adjustTagUsageCount = useAdjustTagUsageCount();
    const [editingMode, setEditingMode] = useState(false);
    const [itemToModify, setItemToModify] = useState(null);
    const [showDocuments, setShowDocuments] = useState(showDocumentsByDefault ? true : false);
    //const Documents = event.Documents ? event.Documents : [];
    const docstorender = type === 'eventGroup' ? event.GroupDocuments : event.Documents;
    const [touchForm, setTouchForm] = useState(false);

    let initialValues = {
        Title: '',
        Notes: '',
        IsHidden: false,
    };

    const handleShowDocuments = () => {
        setShowDocuments((prevShow) => !prevShow);
    };

    if (itemToModify) {
        const index = docstorender.findIndex((el) => el.ID === itemToModify);
        if (index > -1) {
            const Title = docstorender[index].Title;
            const Notes = docstorender[index].Notes || '';
            const IsHidden = docstorender[index].IsHidden || false;
            initialValues = { Title, Notes, IsHidden };
        }
    }

    const editDocument = (values, setSubmitting) => {
        setSubmitting(true);
        Axios.put(`/documents/${itemToModify}`, [
            { name: 'Title', value: values.Title, changed: true },
            { name: 'Notes', value: values.Notes, changed: true },
            { name: 'IsHidden', value: values.IsHidden || false, changed: true },
        ])
            .then(() => {
                setTouchForm(false);
                setItemToModify(null);
                setSubmitting(false);
                setEditingMode(false);
                updateEvent();
            })
            .catch((error) => {
                //TODO: manage error on api call, may be by adding an ErrorMessage to the state and a condition to show it in render.
                console.error('errore', error);
            });
    };

    const deleteDocument = (documentToDelete) => {
        Axios.delete(`/documents/?ids=${documentToDelete.ID}`)
            .then(() => {
                // il documento eliminato non usa piu' i suoi tag
                (documentToDelete.Tags || []).forEach((tag) => adjustTagUsageCount(tag.ID, -1));
                updateEvent();
            })
            .catch((error) => {
                console.error('Errore', error);
                //TODO: manage error on api call, may be by adding an ErrorMessage to the state and a condition to show it in render.
            });
    };

    const onOpenDocument = (document) => {
        window.open(getUrlDocument(document), '_blank');
    };

    const onEditDocument = (document) => {
        setItemToModify(document.ID);
        setEditingMode(true);
    };

    const getDescription = (document) => {
        if (document.IsHidden) {
            return (
                <>
                    <span className="doc-hidden">
                        <NwIcon icon={byPrefixAndName.fasl['eye-slash']} /> Hidden from models/talents
                    </span>
                    <CreatedBy object={document} short textonly />
                </>
            );
        }
        return <CreatedBy object={document} short textonly />;
    };

    const actions = (document) => {
        return [
            <NwDropDownMenu
                key={`event-${document.ID}`}
                actions={[
                    {
                        name: '',
                        key: 'edit',
                        pinned: true,
                        icon: byPrefixAndName.fasl['pen'],
                        action: () => onEditDocument(document),
                    },
                    {
                        name: '',
                        key: 'download',
                        pinned: true,
                        icon: byPrefixAndName.fasl['cloud-download'],
                        action: () => onOpenDocument(document),
                    },
                    {
                        name: 'delete',
                        type: 'delete',
                        key: 'delete',
                        icon: byPrefixAndName.fasl['trash-alt'],
                        confirmationPlacement: 'left',
                        action: () => deleteDocument(document),
                    },
                ]}
            />,
        ];
    };

    const renderDocumentForm = () => {
        return (
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    Title: Yup.string().required('Content is required'),
                })}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    setStatus();
                    editDocument(values, setSubmitting);
                }}
            >
                {({ errors, status, touched, isSubmitting, values, setFieldValue, handleSubmit }) => (
                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                        <div className="form-group">
                            <label htmlFor="Title">Edit document</label>
                            <Input
                                autoComplete="off"
                                value={values.Title}
                                onChange={(e) => setFieldValue('Title', e.target.value)}
                                name="Title"
                                className={'form-control' + (errors.Title && touched.Title ? ' is-invalid' : '')}
                            />
                            <ErrorMessage
                                name="Title"
                                className="invalid-feedback"
                                render={(msg) => <div>{msg}</div>}
                            />
                            <br />
                        </div>
                        <br />
                        <div className="form-group">
                            <TextAreaWithCharCounter
                                autoComplete="off"
                                value={values.Notes}
                                onChange={(e) => setFieldValue('Notes', e.target.value)}
                                name="Notes"
                                className={'form-control' + (errors.Notes && touched.Notes ? ' is-invalid' : '')}
                                maxLength={2000}
                                autoSize={{ minRows: 5, maxRows: 10 }}
                            />
                            <ErrorMessage
                                name="Notes"
                                className="invalid-feedback"
                                render={(msg) => <div>{msg}</div>}
                            />
                        </div>
                        <br />
                        <div className="form-group">
                            <CustomSwitch
                                text="Hide from models/talents"
                                value={values.IsHidden}
                                onChange={(value) => setFieldValue('IsHidden', value)}
                            />
                        </div>
                        <br />
                        <div className="form-group">
                            <NwFormButtonsBar
                                left={
                                    <NwCancelButton
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setTouchForm(false);
                                            setEditingMode(false);
                                            setItemToModify(null);
                                        }}
                                    />
                                }
                                right={
                                    <NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />
                                }
                            />
                        </div>
                        {status && (
                            <div className={'alert alert-danger'}>
                                <React.Fragment>{status.msg}</React.Fragment>
                            </div>
                        )}
                    </NwForm>
                )}
            </Formik>
        );
    };

    return (
        <>
            {editingMode && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    title="Edit Document"
                    onClose={() => setEditingMode(false)}
                >
                    {renderDocumentForm()}
                </NwDrawer>
            )}
            {docstorender && docstorender.length !== 0 && (
                <Wrapper>
                    <NwDivider
                        dark
                        onClick={handleShowDocuments}
                        icon={
                            showDocuments ? byPrefixAndName.fasl['chevron-down'] : byPrefixAndName.fasl['chevron-right']
                        }
                    >
                        Documents
                        <Badge count={docstorender.length} />
                    </NwDivider>
                    {showDocuments && (
                        <Container gutter={16}>
                            {docstorender.map((document) => {
                                return (
                                    <Col span={singleColumn ? 24 : 12} key={`document-${document.ID}`}>
                                        <DocumentCard className={singleColumn ? 'single-column' : ''}>
                                            <div className="document-card-body">
                                                <NwIcon
                                                    style={{ fontSize: 24 }}
                                                    icon={getFileTypeIcons(document.Extension)}
                                                />
                                                <FlexContainer
                                                    className="content"
                                                    mg="0 8px"
                                                    column
                                                    alignItems="flex-start"
                                                >
                                                    <div className="title">{document.Title}</div>
                                                    <div className="description">{getDescription(document)}</div>
                                                </FlexContainer>
                                            </div>
                                            <div className="document-card-footer">{actions(document)}</div>
                                        </DocumentCard>
                                    </Col>
                                );
                            })}
                        </Container>
                    )}
                </Wrapper>
            )}
        </>
    );
};

export default EventDocuments;
